import { createEvoClient, createFeedbackVote, getFeedbackVote, getFeedbackVoteSummary } from "@jti/evo-client";
import { getConfig } from "@jti/fe-common";

import { getUser } from "../../utils";
import type { RestResponseType } from "../Rest";

export type FeedbackVoteSummary = {
  url: string;
  likes: number;
  dislikes: number;
  total: number;
};

export type FeedbackVote = {
  vote_type: boolean;
  content_type: "website" | "product";
  content: {
    url: string;
    reference_type: "faq_page" | "video";
    reference_name: string;
  };
  created_at: string;
};

export type FeedbackVoteMeta = {
  can_vote: boolean;
  votes_left_today: number;
};

export type CreateFeedbackVotePayload = Omit<FeedbackVote, "created_at">;

const USER_TOKEN_HEADER = "X-DES-USER-TOKEN";

export class FeedbackVotesApi {
  private readonly client;
  private readonly ciamToken: string;

  protected constructor(baseUrl: string, tenantToken: string, recaptchaId: string, ciamToken: string) {
    this.ciamToken = ciamToken;
    this.client = createEvoClient({
      baseUrl,
      throwOnError: false,
      tenantToken,
      recaptchaId,
      ciamToken,
    });
  }

  static init() {
    const { ciam_token } = getUser();
    const { EVO_DOMAIN, EVO_TENANT, ["X-RECAPTCHA-ID"]: recaptchaId } = getConfig();
    return new FeedbackVotesApi(EVO_DOMAIN, EVO_TENANT, recaptchaId, ciam_token);
  }

  async getVote(
    url: string,
    referenceType: "faq_page" | "video",
    referenceName: string
  ): Promise<RestResponseType<{ data?: FeedbackVote; meta?: FeedbackVoteMeta }>> {
    const { data, error, response } = await getFeedbackVote({
      client: this.client,
      query: { url, reference_type: referenceType, reference_name: referenceName },
      headers: {
        [USER_TOKEN_HEADER]: this.ciamToken,
      },
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    return {
      data: data?.data,
      meta: data?.meta,
      statusCode: response.status,
    };
  }

  async createVote(payload: CreateFeedbackVotePayload): Promise<RestResponseType<{ meta?: FeedbackVoteMeta }>> {
    const { data, error, response } = await createFeedbackVote({
      client: this.client,
      body: payload,
      headers: {
        [USER_TOKEN_HEADER]: this.ciamToken,
      },
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    return {
      meta: data?.meta,
      statusCode: response.status,
    };
  }

  async getVoteSummary(
    url: string,
    referenceType: "faq_page" | "video",
    referenceName: string,
    filter?: { created_at?: { eq?: string; neq?: string; gt?: string; gte?: string; lt?: string; lte?: string } }
  ): Promise<RestResponseType<{ data?: FeedbackVoteSummary; meta?: FeedbackVoteMeta }>> {
    const { data, error, response } = await getFeedbackVoteSummary({
      client: this.client,
      query: {
        url,
        reference_type: referenceType,
        reference_name: referenceName,
        ...(filter ? { filter } : {}),
      },
    });

    if (!response.ok) {
      throw { ...error, statusCode: response.status };
    }

    return {
      data: data?.data,
      meta: (data as { meta?: FeedbackVoteMeta } | undefined)?.meta,
      statusCode: response.status,
    };
  }
}
